import { Divider, List, ListSubheader, Collapse } from "@mui/material";
import { Fragment } from "react";
import {
  Sidebar as RASidebar,
  type SidebarProps,
  useResourceDefinitions,
  type ResourceDefinition,
  type ResourceOptions,
  Menu,
  useSidebarState,
  usePermissions,
} from "react-admin";

import {
  type SidebarGroup,
  SIDEBAR_GROUPS,
  SIDEBAR_NAMES,
} from "@/constants/sidebar";
import { getAccessName } from "@/providers/authProvider/utils";

type ExtendedResourceOptions = ResourceOptions & {
  path?: string;
  label?: string;
  group?: SidebarGroup;
};

type ExtendedResourceDefinition = ResourceDefinition<ExtendedResourceOptions>;

type GroupedResources = {
  group: SidebarGroup;
  resources: ExtendedResourceDefinition[];
};

export const getGroupedResources = (
  resources: ExtendedResourceDefinition[],
): GroupedResources[] => {
  return SIDEBAR_GROUPS.map((groupKey) => ({
    group: groupKey,
    resources: resources.filter((r) => r.options?.group === groupKey),
  })).filter((grouped) => grouped.resources.length > 0);
};

export const Sidebar = (props: SidebarProps) => {
  const [open] = useSidebarState();
  const { permissions } = usePermissions<string[]>();
  const rootResources = useResourceDefinitions<ExtendedResourceOptions>();
  const filteredResources = Object.values(rootResources).filter(
    (resource) =>
      !permissions?.includes(getAccessName(resource.name, "list")) ||
      permissions.includes(getAccessName(resource.name, "show")),
  );
  const groupedResources = getGroupedResources(filteredResources);

  return (
    <RASidebar {...props}>
      <List>
        {groupedResources.map(({ group, resources }, index) => {
          const showDivider = index < groupedResources.length - 1;

          return (
            <Fragment key={group}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <ListSubheader
                  sx={{
                    background: (theme) => theme.palette.background.default,
                  }}
                >
                  {SIDEBAR_NAMES[group]}
                </ListSubheader>
              </Collapse>
              {resources.map(
                // eslint-disable-next-line @typescript-eslint/naming-convention
                ({ icon: IconComponent, ...resource }) =>
                  resource.options?.path ? (
                    <Menu.Item
                      key={resource.name}
                      to={resource.options.path}
                      primaryText={resource.options.label}
                      leftIcon={IconComponent ? <IconComponent /> : undefined}
                    />
                  ) : (
                    <Menu.ResourceItem
                      name={resource.name}
                      key={resource.name}
                    />
                  ),
              )}
              {showDivider && <Divider />}
            </Fragment>
          );
        })}
      </List>
    </RASidebar>
  );
};
